import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck } from 'lucide-react';
import { bookingAPI, getImageUrl } from '../../lib/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: { color: '#D97706', bg: '#FFFBEB' },
  confirmed: { color: '#059669', bg: '#ECFDF5' },
  checked_in: { color: '#2563EB', bg: '#EFF6FF' },
  checked_out: { color: '#6B7280', bg: '#F3F4F6' },
  cancelled: { color: '#DC2626', bg: '#FEF2F2' },
};

const GuestBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');

  useEffect(() => {
    bookingAPI.getAll({ limit: 50 })
      .then(res => setBookings(res.data.data || []))
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();
  const upcoming = bookings.filter(b => new Date(b.checkIn) >= now && b.status !== 'cancelled');
  const past = bookings.filter(b => b.status === 'checked_out' || (new Date(b.checkOut) < now && b.status !== 'cancelled'));
  const cancelled = bookings.filter(b => b.status === 'cancelled');

  const lists = { upcoming, past, cancelled };
  const current = lists[tab] || [];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Bookings</h1>
          <p className="page-subtitle">{bookings.length} total bookings</p>
        </div>
      </div>

      <div className="tabs">
        {[['upcoming', `Upcoming (${upcoming.length})`], ['past', `Past (${past.length})`], ['cancelled', `Cancelled (${cancelled.length})`]].map(([key, label]) => (
          <button key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : current.length === 0 ? (
        <div className="empty-state">
          <CalendarCheck size={48} />
          <h3>No {tab} bookings</h3>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {current.map(b => {
            const coverImg = b.property?.images?.find(i => i.isCover) || b.property?.images?.[0];
            return (
              <div key={b._id} className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex' }}>
                <div style={{ width: 160, minHeight: 130, background: '#F3F4F6', flexShrink: 0 }}>
                  {coverImg?.url ? (
                    <img src={getImageUrl(coverImg.url)} alt={b.property?.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#EFF6FF' }}>
                      <CalendarCheck size={32} color="#93C5FD" />
                    </div>
                  )}
                </div>
                <div style={{ padding: 18, flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{b.property?.title}</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>
                      {b.checkIn ? format(new Date(b.checkIn), 'MMM d') : ''} – {b.checkOut ? format(new Date(b.checkOut), 'MMM d, yyyy') : ''} · {b.nights} nights
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span className="badge" style={{ color: STATUS_STYLES[b.status]?.color, background: STATUS_STYLES[b.status]?.bg }}>
                        {b.status?.replace('_', ' ')}
                      </span>
                      <span className={`badge ${b.payment?.status === 'paid' ? 'badge-green' : 'badge-yellow'}`}>
                        {b.payment?.status}
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                      ${b.pricing?.total?.toLocaleString()}
                    </div>
                    {(b.status === 'confirmed' || b.status === 'checked_in') && (
                      <button className="btn btn-sm btn-primary" onClick={() => navigate(`/guest/check-in/${b._id}`)}>
                        Check-in Info
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GuestBookings;
